import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Link,
  VStack,
  Divider,
  Image
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';

const Login = ({ onLogin }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [allowGoogleLogin, setAllowGoogleLogin] = useState(false);
  const [allowTiktokLogin, setAllowTiktokLogin] = useState(false);
  const [allowInstagramLogin, setAllowInstagramLogin] = useState(false);
  const [allowFacebookLogin, setAllowFacebookLogin] = useState(false);

  const oauthIconByProvider = {
    google: '/assets/RRSS/google_color.svg',
    facebook: '/assets/RRSS/facebook_color.png',
    instagram: '/assets/RRSS/instagram_color.svg',
    tiktok: '/assets/RRSS/tiktok_color.svg',
  };

  useEffect(() => {
    const fetchLoginProviders = async () => {
      try {
        const API_BASE_URL = getApiBaseUrl();
        const response = await fetch(`${API_BASE_URL}/api/settings/public/login-providers`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        setAllowGoogleLogin(Boolean(data?.permitir_login_google));
        setAllowTiktokLogin(Boolean(data?.permitir_login_tiktok));
        setAllowInstagramLogin(Boolean(data?.permitir_login_instagram));
        setAllowFacebookLogin(Boolean(data?.permitir_login_facebook));
      } catch (err) {
        console.error('[LOGIN] Error loading login providers:', err);
      }
    };

    fetchLoginProviders();
  }, []);

  const handleGoogleLogin = (role) => {
    const API_BASE_URL = getApiBaseUrl();
    window.location.href = `${API_BASE_URL}/api/auth/google?role=${encodeURIComponent(role)}`;
  };

  const handleTiktokLogin = (role) => {
    const API_BASE_URL = getApiBaseUrl();
    window.location.href = `${API_BASE_URL}/api/auth/tiktok?role=${encodeURIComponent(role)}`;
  };

  const handleInstagramLoginForRole = (role) => {
    const API_BASE_URL = getApiBaseUrl();
    window.location.href = `${API_BASE_URL}/api/auth/instagram?role=${encodeURIComponent(role)}`;
  };

  const handleFacebookLogin = (role) => {
    const API_BASE_URL = getApiBaseUrl();
    window.location.href = `${API_BASE_URL}/api/auth/facebook?role=${encodeURIComponent(role)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const API_BASE_URL = getApiBaseUrl();

      // Generar o recuperar custom_device_id
      let customDeviceId = localStorage.getItem('custom_device_id');
      if (!customDeviceId) {
        customDeviceId = `device-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem('custom_device_id', customDeviceId);
        console.log(`[LOGIN] ✓ Nuevo custom_device_id generado: ${customDeviceId}`);
      } else {
        console.log(`[LOGIN] ℹ Usando custom_device_id existente: ${customDeviceId}`);
      }

      const device_info = {
        user_agent: navigator.userAgent || null,
        accept_language: navigator.language || null,
        app_version: navigator.appVersion || null,
        platform: navigator.platform || null,
        vendor: navigator.vendor || null,
        ip_address: null,
        ip_address_forwarded: null,
        origin: window.location.origin,
        referer: document.referrer || null,
        connection_type: navigator.connection ? navigator.connection.effectiveType : null,
        accept_encoding: null,
        x_requested_with: "XMLHttpRequest",
        custom_device_id: customDeviceId
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/sesiones/login/`,
        { email, password, device_info }
      );

      const { access_token, session_id, user_id, user_type, email: userEmail } = response.data;

      localStorage.setItem('auth-token', access_token);
      localStorage.setItem('session_id', session_id);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('user_type', user_type);
      localStorage.setItem('user_email', userEmail);

      if (onLogin) onLogin(response.data);
      login(response.data);

      // Redirigir a la pantalla de bienvenida después del login
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || t('login_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={4}>
      <Heading as="h2" size="lg" mb={4}>
        {t('login_titulo')}
      </Heading>
      <Box as="form" onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl id="email">
            <FormLabel>{t('login_email')}</FormLabel>
            <Input
              type="email"
              placeholder={t('login_email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </FormControl>
          <FormControl id="password">
            <FormLabel>{t('login_password')}</FormLabel>
            <Input
              type="password"
              placeholder={t('login_password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormControl>
          <Button type="submit" colorScheme="blue" isLoading={loading}>
            {loading ? t('login_accediendo') : t('login_acceder')}
          </Button>
          <Link as={RouterLink} to="/registro-cliente" color="blue.600" textAlign="center">
            {t('login_crear_usuario')}
          </Link>
          {error && (
            <Text color="red.500" mt={2}>
              {error}
            </Text>
          )}
          {(allowGoogleLogin || allowTiktokLogin || allowInstagramLogin || allowFacebookLogin) && (
            <>
              <Divider />
              <Text textAlign="center" color="gray.600">
                {t('login_multi_o')}
              </Text>
              {allowGoogleLogin && (
                <Button
                  className="oauth-provider-button oauth-provider-google"
                  onClick={() => handleGoogleLogin('cliente')}
                  leftIcon={<Image src={oauthIconByProvider.google} alt="Google" boxSize="18px" objectFit="contain" />}
                >
                  {t('login_multi_google')}
                </Button>
              )}
              {allowFacebookLogin && (
                <Button
                  className="oauth-provider-button oauth-provider-facebook"
                  onClick={() => handleFacebookLogin('cliente')}
                  leftIcon={<Image src={oauthIconByProvider.facebook} alt="Facebook" boxSize="18px" objectFit="contain" />}
                >
                  {t('login_multi_facebook')}
                </Button>
              )}
              {allowInstagramLogin && (
                <Button
                  className="oauth-provider-button oauth-provider-instagram"
                  onClick={() => handleInstagramLoginForRole('cliente')}
                  leftIcon={<Image src={oauthIconByProvider.instagram} alt="Instagram" boxSize="18px" objectFit="contain" />}
                >
                  {t('login_multi_instagram')}
                </Button>
              )}
              {allowTiktokLogin && (
                <Button
                  className="oauth-provider-button oauth-provider-tiktok"
                  onClick={() => handleTiktokLogin('cliente')}
                  leftIcon={<Image src={oauthIconByProvider.tiktok} alt="TikTok" boxSize="18px" objectFit="contain" />}
                >
                  {t('login_multi_tiktok')}
                </Button>
              )}
            </>
          )}
        </VStack>
      </Box>
      <Box mt={4} textAlign="center">
        <Link as={RouterLink} to="/login-conductor" color="blue.600">
          {t('login_entrar_conductor', { defaultValue: 'Entrar como conductor' })}
        </Link>
      </Box>
    </Box>
  );
};

export default Login;